import React, { useState } from 'react';
import {
  UseFieldArrayReturn,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import styles from '../DefaultTemplate.module.scss';
import { QualityAssessmentTemplateState } from '../../QualityAssessmentTemplate';
import QaConfirmationDialog from '../../QaConfirmationDialog';
import SectionTitle from './SectionTitle';
import DomainForm from './DomainForm';
import { Icon, IconList, Accordion, Link } from 'components/core';

const DEFAULT_JUDGEMENTS = ['High', 'Low', 'Unclear'];

const DomainsSection = () => {
  const domains = useFieldArray<QualityAssessmentTemplateState>({
    name: 'domains',
  }) as UseFieldArrayReturn<QualityAssessmentTemplateState, 'domains'>;

  const {
    getValues,
    formState: { errors },
  } = useFormContext();
  const hasDomainErrors = !!errors?.domains;

  const [modal, setModal] = useState<React.ReactElement | null>();

  const createDomain = (
    judgementLabels: string[]
  ): QualityAssessmentTemplateState['domains'][number] => {
    const newDomain = {
      name: '',
      description: '',
      outcome_specific: false,
      prompt_before_deleting: false,
    } as QualityAssessmentTemplateState['domains'][number];

    newDomain.judgements = judgementLabels.map((label, index) => ({
      label,
      position: index + 1,
    }));

    return newDomain;
  };

  const appendDomain = (judgementLabels: string[]) => {
    domains.append(createDomain(judgementLabels));
  };

  const addDomain = () => {
    appendDomain(DEFAULT_JUDGEMENTS);
  };

  const duplicateDomain = (index: number) => {
    const sourceDomain = getValues(
      `domains.${index}`
    ) as QualityAssessmentTemplateState['domains'][number];
    if (!sourceDomain) return;

    appendDomain(
      (sourceDomain.judgements || []).map((judgement) => judgement.label || '')
    );
  };

  return (
    <section className={styles.DefaultTemplate_section}>
      {modal}
      <SectionTitle
        title="Domains"
        pendoKey="qat-domains"
        ariaLabel="Get help with domains"
      />

      <Accordion className={styles.DomainAccordion} allowMultiple={false}>
        {domains.fields.map((field, index) => {
          return (
            <DomainForm
              key={field.id}
              name={`domains.${index}`}
              disableDuplicate={hasDomainErrors}
              onDuplicate={() => duplicateDomain(index)}
              onDelete={() => {
                if (field.prompt_before_deleting) {
                  setModal(
                    <QaConfirmationDialog
                      onConfirm={() => domains.remove(index)}
                      onClose={() => setModal(null)}
                    />
                  );
                  return;
                }
                domains.remove(index);
              }}
            />
          );
        })}
      </Accordion>

      <Link
        onClick={addDomain}
        className={styles.SectionButton}
        variant="primary"
        disabled={hasDomainErrors}
        data-testid="add-domain"
      >
        <Icon icon={IconList.light.faPlus} /> Add domain
      </Link>
    </section>
  );
};

export default DomainsSection;
